import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import styles from './SeasonTimeline.module.css'

export type SeasonTimelineSegmentKind =
  | 'registration'
  | 'pre-season'
  | 'matches'
  | 'holiday'
  | 'playoffs'

export interface SeasonTimelineSegment {
  id: string
  kind: SeasonTimelineSegmentKind
  startsAt: string
  endsAt: string
  label?: string
  variant?: 'auto' | 'manual'
}

export interface SeasonTimelineProps {
  segments: SeasonTimelineSegment[]
  now?: string
  showProgress?: boolean
  /** Render a date row under the track, aligned to each phase boundary. */
  showDates?: boolean
  /** Label for the in-range progress marker (default "Today"). */
  nowLabel?: string
  highlightSegmentId?: string
  onSegmentClick?: (id: string) => void
  compact?: boolean
  emptyMessage?: string
  className?: string
}

function formatShortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

function formatFullDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const DAY_MS = 1000 * 60 * 60 * 24

function durationLabel(startISO: string, endISO: string): string {
  const days = Math.max(
    1,
    Math.round((new Date(endISO).getTime() - new Date(startISO).getTime()) / DAY_MS),
  )
  if (days >= 14 && days % 7 === 0) {
    const weeks = days / 7
    return `${weeks}w`
  }
  return `${days}d`
}

const KIND_LABELS: Record<SeasonTimelineSegmentKind, string> = {
  registration: 'Registration',
  'pre-season': 'Pre-season',
  matches: 'Regular Season',
  holiday: 'Holiday',
  playoffs: 'Playoffs',
}

const KIND_SHORT_LABELS: Record<SeasonTimelineSegmentKind, string> = {
  registration: 'Reg',
  'pre-season': 'Pre',
  matches: 'Matches',
  holiday: 'Bye',
  playoffs: 'Playoffs',
}

export function SeasonTimeline({
  segments,
  now,
  showProgress = false,
  showDates = false,
  nowLabel = 'Today',
  highlightSegmentId,
  onSegmentClick,
  compact = false,
  emptyMessage = 'Pick a start and end date to see your season timeline',
  className,
}: SeasonTimelineProps) {
  const nowMs = useMemo(() => (now ? new Date(now).getTime() : Date.now()), [now])

  const { spans, rangeStart, rangeEnd, hasContent } = useMemo(() => {
    const valid = segments.filter((s) => {
      if (!s.startsAt || !s.endsAt) return false
      const a = new Date(s.startsAt).getTime()
      const b = new Date(s.endsAt).getTime()
      return !isNaN(a) && !isNaN(b) && b > a
    })
    if (valid.length === 0) {
      return { spans: [], rangeStart: 0, rangeEnd: 0, hasContent: false }
    }
    const starts = valid.map((s) => new Date(s.startsAt).getTime())
    const ends = valid.map((s) => new Date(s.endsAt).getTime())
    const rStart = Math.min(...starts)
    const rEnd = Math.max(...ends)
    const spans = valid.map((seg) => {
      const segStart = new Date(seg.startsAt).getTime()
      const segEnd = new Date(seg.endsAt).getTime()
      return {
        seg,
        segStart,
        segEnd,
        durationDays: Math.max(1, (segEnd - segStart) / DAY_MS),
      }
    })
    return { spans, rangeStart: rStart, rangeEnd: rEnd, hasContent: true }
  }, [segments])

  // Today marker position needs the actual rendered geometry (flex with
  // min-widths means visual position isn't a pure function of time). We
  // read segment bounds after layout and translate "now" into a pixel offset.
  const trackRef = useRef<HTMLDivElement>(null)
  const segmentRefs = useRef<Map<string, HTMLElement>>(new Map())
  const [marker, setMarker] = useState<{ px: number; mode: 'before' | 'in' | 'after' } | null>(null)

  // Hovering (or controlled-highlighting) a phase emphasizes both the bar
  // segment and its boundary dates, making the timeline feel interactive.
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const activeId = hoveredId ?? highlightSegmentId ?? null
  const lastSpanId = spans.length > 0 ? spans[spans.length - 1].seg.id : null

  useLayoutEffect(() => {
    if (!showProgress || !hasContent || !trackRef.current) {
      setMarker(null)
      return
    }
    const trackRect = trackRef.current.getBoundingClientRect()
    // Out of range: pin the marker to the edge so there's always a "you are
    // here" anchor. Before the season the left edge is next-up; after it ends
    // everything reads as past (handled by the .past shading below).
    if (nowMs < rangeStart) {
      setMarker({ px: 0, mode: 'before' })
      return
    }
    if (nowMs > rangeEnd) {
      setMarker({ px: Math.max(0, trackRect.width - 1), mode: 'after' })
      return
    }
    let resolved: number | null = null
    for (const span of spans) {
      const node = segmentRefs.current.get(span.seg.id)
      /* v8 ignore next -- sentinel node is always rendered when the effect runs */
      if (!node) continue
      const rect = node.getBoundingClientRect()
      if (nowMs >= span.segStart && nowMs <= span.segEnd) {
        const ratio = (nowMs - span.segStart) / (span.segEnd - span.segStart)
        resolved = rect.left - trackRect.left + ratio * rect.width
        break
      }
    }
    setMarker(resolved !== null ? { px: resolved, mode: 'in' } : null)
  }, [showProgress, hasContent, nowMs, rangeStart, rangeEnd, spans])

  if (!hasContent) {
    return (
      <div
        className={[styles.root, compact ? styles.compact : '', styles.empty, className]
          .filter(Boolean)
          .join(' ')}
        role="img"
        aria-label="Empty season timeline"
      >
        <span className={styles.emptyText}>{emptyMessage}</span>
      </div>
    )
  }

  return (
    <div
      className={[styles.root, compact ? styles.compact : '', className].filter(Boolean).join(' ')}
      role="group"
      aria-label="Season timeline"
    >
      <div className={styles.track} ref={trackRef}>
        {spans.map(({ seg, segEnd, durationDays }) => {
          const isPast = showProgress && segEnd <= nowMs
          const isHighlighted = activeId === seg.id
          const segClasses = [
            styles.segment,
            styles[`kind_${seg.kind}`],
            seg.variant === 'auto' ? styles.auto : '',
            isPast ? styles.past : '',
            isHighlighted ? styles.highlighted : '',
            onSegmentClick ? styles.interactive : '',
          ]
            .filter(Boolean)
            .join(' ')

          const fullLabel = seg.label ?? KIND_LABELS[seg.kind]
          const shortLabel = KIND_SHORT_LABELS[seg.kind]
          const dur = durationLabel(seg.startsAt, seg.endsAt)
          const title = `${fullLabel} · ${formatFullDate(seg.startsAt)} → ${formatFullDate(seg.endsAt)} (${dur})`

          // Pre-season / bye gaps stay short — single line, no duration.
          const isGap = seg.kind === 'pre-season' || seg.kind === 'holiday'

          const inner = (
            <span className={styles.segmentInner}>
              <span className={styles.segmentLabelFull}>{fullLabel}</span>
              <span className={styles.segmentLabelShort}>{shortLabel}</span>
              {!isGap && <span className={styles.segmentDuration}>{dur}</span>}
            </span>
          )

          const setRef = (node: HTMLElement | null) => {
            if (node) segmentRefs.current.set(seg.id, node)
            else segmentRefs.current.delete(seg.id)
          }

          const style = {
            flexGrow: durationDays,
            flexShrink: 0,
            flexBasis: 0,
          } as const

          if (onSegmentClick) {
            return (
              <button
                key={seg.id}
                ref={setRef}
                type="button"
                className={segClasses}
                style={style}
                title={title}
                onClick={() => onSegmentClick(seg.id)}
                onMouseEnter={() => setHoveredId(seg.id)}
                onMouseLeave={() => setHoveredId(null)}
                aria-label={title}
                data-segment={seg.id}
              >
                {inner}
              </button>
            )
          }

          return (
            <div
              key={seg.id}
              ref={setRef}
              className={segClasses}
              style={style}
              onMouseEnter={() => setHoveredId(seg.id)}
              onMouseLeave={() => setHoveredId(null)}
              title={title}
              data-segment={seg.id}
            >
              {inner}
            </div>
          )
        })}

        {marker !== null && (
          <div
            className={[styles.nowMarker, marker.mode !== 'in' ? styles.edgeMarker : '']
              .filter(Boolean)
              .join(' ')}
            style={{ left: `${marker.px}px` }}
            aria-label={marker.mode === 'in' ? nowLabel : undefined}
          >
            {marker.mode === 'in' && <span className={styles.nowLabel}>{nowLabel}</span>}
          </div>
        )}
      </div>

      {showDates ? (
        <div className={styles.dateAxis}>
          {spans.map(({ seg, durationDays }) => (
            <span
              key={seg.id}
              className={[styles.dateCell, activeId === seg.id ? styles.dateActive : '']
                .filter(Boolean)
                .join(' ')}
              style={{ flexGrow: durationDays, flexShrink: 0, flexBasis: 0 }}
              onMouseEnter={() => setHoveredId(seg.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {formatShortDate(seg.startsAt)}
            </span>
          ))}
          <span
            className={[styles.dateEnd, activeId !== null && activeId === lastSpanId ? styles.dateActive : '']
              .filter(Boolean)
              .join(' ')}
          >
            {formatShortDate(new Date(rangeEnd).toISOString())}
          </span>
        </div>
      ) : (
        <div className={styles.endpoints}>
          <span className={styles.endpoint}>{formatShortDate(new Date(rangeStart).toISOString())}</span>
          <span className={styles.endpoint}>{formatShortDate(new Date(rangeEnd).toISOString())}</span>
        </div>
      )}
    </div>
  )
}
